import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Wifi, Server, Shield, Smartphone, Code, 
  Target, Headphones, CheckCircle2, MessageSquare, Star,
  Cctv, Sliders, Activity, Wrench, ChevronLeft, ChevronRight,
  Sun, Home as HomeIcon, Laptop
} from 'lucide-react';
import PageHeader from '../../components/PageHeader/PageHeader';
import './Home.css';
import cameraImage from '../../assets/cameraImge.jpg';
import serverImage from '../../assets/serverImage.jpg';

// Reusable custom component to handle bidirectional reveal on scroll
const RevealOnScroll = ({ children, className = '', delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (let i = 0; i < entries.length; i++) {
          setIsVisible(entries[i].isIntersecting);
        }
      },
      { threshold: 0.1 }
    );
    
    const current = domRef.current;
    if (current) observer.observe(current);
    
    return () => {
      if (current) observer.unobserve(current);
    };
  }, []);

  return (
    <div
      ref={domRef}
      className={`reveal-wrapper ${isVisible ? 'is-visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
};

// Custom component for the letter-by-letter typing animation in the Hero
const AnimatedHeroTitle = () => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (let i = 0; i < entries.length; i++) {
          setIsVisible(entries[i].isIntersecting);
        }
      },
      { threshold: 0.1 }
    );
    
    const current = domRef.current;
    if (current) observer.observe(current);
    
    return () => {
      if (current) observer.unobserve(current);
    };
  }, []);

  const line1 = "Securing & Connecting ".split('');
  const line2 = "Your World".split('');

  return (
    <span ref={domRef} className={`hero-title-animated ${isVisible ? 'is-visible' : ''}`} style={{ display: 'block' }}>
      <span className="line-wrap">
        {line1.map((char, i) => (
          <span key={i} className="letter" style={{ transitionDelay: `${i * 0.05}s` }}>
            {char === ' ' ? '\u00A0' : char}
          </span>
        ))}
      </span>
      <br />
      <span className="line-wrap highlight-green">
        {line2.map((char, i) => (
          <span key={`highlight-${i}`} className="letter" style={{ transitionDelay: `${(line1.length + i) * 0.05}s` }}>
            {char === ' ' ? '\u00A0' : char}
          </span>
        ))}
      </span>
    </span>
  );
};

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const services = [
    { title: 'IT Equipment Resale', icon: <Laptop size={32} />, desc: 'Trusted suppliers of enterprise hardware including Dell, Lenovo, HP, and Huawei laptops, desktops, and servers.', animation: 'animate-pulse' },
    { title: 'Solar Installations', icon: <Sun size={32} />, desc: 'Efficient solar energy and backup power solutions to keep your home or enterprise running uninterrupted.', animation: 'animate-float' },
    { title: 'Smart Home Systems', icon: <HomeIcon size={32} />, desc: 'Comprehensive automation and configuration for seamless control of lighting, security, and climate.', animation: 'animate-pulse' },
    { title: 'Security Installations', icon: <Cctv size={32} />, desc: 'Professional installation of advanced CCTV and physical security systems tailored to your premises.', animation: 'animate-pulse' },
    { title: 'System Configuration', icon: <Sliders size={32} />, desc: 'Custom configuration of security networks to ensure flawless integration and maximum coverage.', animation: 'animate-float' },
    { title: '24/7 Monitoring', icon: <Activity size={32} />, desc: 'Round-the-clock security system monitoring to keep your assets protected at all times.', animation: 'animate-pulse' },
    { title: 'Fibre Installations', icon: <Wifi size={32} />, desc: 'High-speed, reliable fibre optic cabling and network setups for residential and commercial spaces.', animation: 'animate-float' },
    { title: 'Maintenance & Support', icon: <Wrench size={32} />, desc: 'Dedicated on-site and remote support to keep your security and fibre networks running perfectly.', animation: 'animate-pulse' },
    { title: 'Web Development', icon: <Code size={32} />, desc: 'Modern, responsive web solutions built to establish your digital presence and optimize operations.', animation: 'animate-float' },
    { title: 'Mobile Apps', icon: <Smartphone size={32} />, desc: 'Native and cross-platform mobile applications for specialized business needs.', animation: 'animate-pulse' },
    { title: 'IT Support', icon: <Shield size={32} />, desc: 'Comprehensive technical troubleshooting and general IT infrastructure upkeep.', animation: 'animate-pulse' },
    { title: 'Secure Hosting', icon: <Server size={32} />, desc: 'Fast, reliable hosting services for your enterprise applications and websites.', animation: 'animate-float' }
  ];

  const features = [
    'Exceptional Customer Service',
    'Tailored Solutions to Meet Unique Needs',
    'Proven Track Record of Success',
    'Competitive Pricing on Top Tech Brands',
    'Continuous Communication and Support',
    'High-Quality, Professional Results'
  ];

  const testimonials = [
    { name: 'IT Director', role: 'Mopani TVET College', text: '"The IT Helpdesk Management System provided by SingularityTech transformed how we handle technical issues across the campus. Their detailed scope of work, thorough execution, and ongoing support have been exceptional."', stars: 5 },
    { name: 'Operations Manager', role: 'Inkspire', text: '"We partnered with SingularityTech for our database infrastructure and digital presence. They delivered highly responsive database scripts and perfectly captured our brand with creative concepts."', stars: 4 },
    { name: 'School Administrator', role: 'Disciplinary Board', text: '"SingularityTech built a customized disciplinary management system that bridged the communication gap between our teachers and parents. The interface is incredibly intuitive for daily school operations."', stars: 5 },
    { name: 'Private Client', role: 'Event Management', text: '"We wanted something completely unique for a traditional Lobola ceremony, and the digital RSVP and contact management system they developed was flawless, reliable, and elegant."', stars: 5 },
    { name: 'Project Partner', role: 'Telkom 10X Hackathon', text: '"Their ability to rapidly develop customer experience solutions is top-tier. A technically sound, highly innovative team that deeply understands modern IT infrastructure and the Sonke Platform."', stars: 4 }
  ];

  const nextSlide = () => {
    setCurrentSlide(currentSlide === testimonials.length - 1 ? 0 : currentSlide + 1);
  };

  const prevSlide = () => {
    setCurrentSlide(currentSlide === 0 ? testimonials.length - 1 : currentSlide - 1);
  };

  const servicesElements = [];
  for (let i = 0; i < services.length; i++) {
    const svc = services[i];
    servicesElements.push(
      <RevealOnScroll key={i} delay={i * 0.1}>
        <div className="service-card">
          <div className={`service-icon-wrap ${svc.animation}`}>
            {svc.icon}
          </div>
          <h3>{svc.title}</h3>
          <p>{svc.desc}</p>
        </div>
      </RevealOnScroll>
    );
  }

  const featuresElements = [];
  for (let j = 0; j < features.length; j++) {
    featuresElements.push(
      <li key={j}>
        <CheckCircle2 size={24} className="why-us-icon" />
        {features[j]}
      </li>
    );
  }

  const carouselSlides = [];
  for (let k = 0; k < testimonials.length; k++) {
    const t = testimonials[k];
    
    const starElements = [];
    for (let s = 0; s < 5; s++) {
      if (s < t.stars) {
        starElements.push(<Star key={s} size={18} fill="currentColor" />);
      } else {
        starElements.push(<Star key={s} size={18} color="currentColor" />);
      }
    }

    carouselSlides.push(
      <div className="carousel-slide" key={k}>
        <div className="testimonial-card">
          <MessageSquare size={60} className="testimonial-quote-icon" />
          <div className="stars">
            {starElements}
          </div>
          <p className="testimonial-text">{t.text}</p>
          <div className="testimonial-author">
            <h4>{t.name}</h4>
            <p>{t.role}</p>
          </div>
        </div>
      </div>
    );
  }

  const indicatorElements = [];
  for (let idx = 0; idx < testimonials.length; idx++) {
    indicatorElements.push(
      <div 
        key={idx} 
        className={`indicator-dot ${currentSlide === idx ? 'active' : ''}`}
        onClick={() => setCurrentSlide(idx)}
      ></div>
    );
  }

  return (
    <div className="home-page">
      <PageHeader 
        breadcrumb="INFRASTRUCTURE, HARDWARE & INNOVATION"
        title={<AnimatedHeroTitle />}
        description="Singularity Tech is a premier provider of physical security systems, high-speed fibre infrastructure, enterprise-grade IT hardware supply, and state-of-the-art solar installations. We secure, power, equip, and connect your business."
        buttonText="Call Now"
        buttonLink="tel:+27663769466"
      />

      {/* --- About Company Section --- */}
      <section className="about-company-section">
        <div className="container about-company-grid">
          <RevealOnScroll className="about-text-content">
            <span className="section-label">ABOUT COMPANY</span>
            <h2>Your Partner for<br/>Security, Power & Hardware</h2>
            <p className="lead-text">
              Singularity Tech is a dedicated team of experts specializing in physical security, energy, and communications infrastructure. We field skilled security technicians, network engineers, solar installers, and dedicated procurement staff.
            </p>
            <p className="lead-text">
              As trusted resellers of top-tier brands like Dell, Lenovo, HP, and Huawei, we supply businesses with reliable laptops, desktops, and networking equipment. Beyond hardware, our seasoned developers craft robust web and mobile applications tailored to your enterprise.
            </p>
            <p className="lead-text">
              We are driven by our commitment to safeguarding your assets, equipping your staff with premium tech, and elevating your digital footprint. Our primary focus is on delivering flawless installations, innovative software solutions, and unwavering 24/7 support.
            </p>
            
            <div className="about-features-row">
              <div className="about-feature">
                <h4><Target size={24} color="var(--brand-green)"/> Experience</h4>
                <p>With an average of over 5 years of experience in the industry, our team is dedicated to delivering exceptional results and exceeding expectations.</p>
              </div>
              <div className="about-feature">
                <h4><Headphones size={24} color="var(--brand-green)"/> Quick Support</h4>
                <p>Our team is dedicated to providing quick and responsive support to ensure your security and network infrastructure remain uncompromised.</p>
              </div>
            </div>

            <Link to="/about">
              <button className="btn-primary">Read More</button>
            </Link>
          </RevealOnScroll>

          <RevealOnScroll className="about-image-wrapper" delay={0.2}>
            <img 
              src={serverImage} 
              alt="Server" 
            />
          </RevealOnScroll>
        </div>
      </section>

      {/* --- Why Choose Us Section --- */}
      <section className="why-us-section">
        <div className="container why-us-grid">
          <RevealOnScroll className="why-us-image">
            <img 
              src={cameraImage} 
              alt="Camera" 
            />
          </RevealOnScroll>
          
          <RevealOnScroll delay={0.2}>
            <span className="section-label">EXPERIENCE. EXECUTION. EXCELLENCE.</span>
            <h2 className="section-title">What Makes Us Different</h2>
            <ul className="why-us-list">
              {featuresElements}
            </ul>
            <a href="tel:+27663769466" style={{ textDecoration: 'none' }}>
              <button className="btn-primary">Call Now</button>
            </a>
          </RevealOnScroll>
        </div>
      </section>

      {/* --- Services Section --- */}
      <section className="services-section">
        <div className="container">
          <RevealOnScroll>
            <span className="section-label">OUR SERVICES</span>
            <h2 className="section-title">Comprehensive Hardware & IT Solutions</h2>
          </RevealOnScroll>
          
          <div className="services-grid">
            {servicesElements}
          </div>

          <RevealOnScroll>
            <Link to="/services">
              <button className="btn-outline">All Services</button>
            </Link>
          </RevealOnScroll>
        </div>
      </section>

      {/* --- Testimonials Carousel Section --- */}
      <section className="testimonials-section">
        <div className="container">
          <RevealOnScroll>
            <span className="section-label">OUR CLIENTS</span>
            <h2 className="section-title">We are Trusted<br/>by Organizations Worldwide</h2>
          </RevealOnScroll>

          <RevealOnScroll delay={0.2}>
            <div className="carousel-container">
              <div 
                className="carousel-track" 
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {carouselSlides}
              </div>
              
              <div className="carousel-controls">
                <button className="carousel-btn prev" onClick={prevSlide}>
                  <ChevronLeft size={24} />
                </button>
                <div className="carousel-indicators">
                  {indicatorElements}
                </div>
                <button className="carousel-btn next" onClick={nextSlide}>
                  <ChevronRight size={24} />
                </button>
              </div>
            </div>
          </RevealOnScroll>
        </div>
      </section>

    </div>
  );
}